import React, { useState } from 'react';
import { teacherRecoverUsername, teacherRequestOtp, teacherVerifyOtp, teacherResetPassword } from '../storage';

// ── TeacherRecovery — credential recovery for teacher portal ──────
function TeacherRecovery({ step, setStep, username, setUsername, otp, setOtp, pw, setPw, pwConf, setPwConf, loading, setLoading, err, setErr, maskedEmail, setMaskedEmail, onBack }) {
  const s = { fontFamily: 'Inter,sans-serif', width: '100%' };
  const inp = { width: '100%', padding: '12px 14px', borderRadius: 12, border: '1.5px solid #e2e8f0', background: '#f8fafc', fontSize: 13, color: '#0f172a', outline: 'none', boxSizing: 'border-box', marginBottom: 10 };
  const btn = (bg) => ({ width: '100%', padding: '13px', borderRadius: 12, border: 'none', background: bg || 'linear-gradient(135deg,#1e293b,#334155)', color: '#fff', fontWeight: 800, fontSize: 14, cursor: 'pointer', marginBottom: 8 });

  // identifier is email for recovery
  const [email, setEmail] = useState('');

  const doRecoverUsername = async () => {
    if (!email) { setErr('Enter your registered email'); return; }
    setLoading(true); setErr('');
    try {
      const r = await teacherRecoverUsername(email);
      if (r.noEmail) { setStep('no-email'); } else { setStep('username-sent'); }
    } catch (e) { setErr(e.message || 'Failed. Please try again.'); }
    finally { setLoading(false); }
  };

  const doRequestOtp = async () => {
    if (!username) { setErr('Enter your portal username'); return; }
    setLoading(true); setErr('');
    try {
      const r = await teacherRequestOtp(username);
      if (r.noEmail) { setStep('no-email'); } else { setMaskedEmail(r.maskedEmail || ''); setStep('otp-entry'); }
    } catch (e) { setErr(e.message || 'Failed. Please try again.'); }
    finally { setLoading(false); }
  };

  const doVerifyOtp = async () => {
    if (!otp) { setErr('Enter the OTP'); return; }
    setLoading(true); setErr('');
    try {
      await teacherVerifyOtp(username, otp);
      setStep('new-password');
    } catch (e) { setErr(e.message || 'Invalid OTP'); }
    finally { setLoading(false); }
  };

  const doResetPassword = async () => {
    if (!pw || !pwConf) { setErr('Fill both fields'); return; }
    if (pw !== pwConf) { setErr('Passwords do not match'); return; }
    if (pw.length < 6) { setErr('Password must be at least 6 characters'); return; }
    setLoading(true); setErr('');
    try {
      await teacherResetPassword(username, otp, pw);
      setStep('success');
    } catch (e) { setErr(e.message || 'Failed. Please try again.'); }
    finally { setLoading(false); }
  };

  const card = (children) => (
    <div style={{ ...s, background: '#fff', borderRadius: 16, padding: '28px 24px', boxShadow: '0 4px 24px rgba(0,32,69,0.1)', border: '1px solid #e2e8f0' }}>
      {children}
    </div>
  );
  const title = (t, sub) => (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a', marginBottom: 4 }}>{t}</div>
      {sub && <div style={{ fontSize: 12, color: '#64748b' }}>{sub}</div>}
    </div>
  );
  const errBox = err ? <div style={{ background: '#fef2f2', color: '#dc2626', borderRadius: 10, padding: '9px 12px', fontSize: 12, fontWeight: 600, marginBottom: 12, border: '1px solid #fecaca' }}>{err}</div> : null;

  if (step === 'choose') return card(<>
    {title('Recover Staff Credentials', 'Choose what you need help with')}
    {errBox}
    <div style={{ marginBottom: 16 }}>
      <label style={{ fontSize: 11, fontWeight: 700, color: '#475569', display: 'block', marginBottom: 6 }}>Registered Email</label>
      <input style={inp} value={email} onChange={e => { setEmail(e.target.value); setErr(''); }} placeholder="Enter your registered email" type="email" onKeyDown={e => e.key === 'Enter' && doRecoverUsername()} />
      <button style={btn('linear-gradient(135deg,#1960a3,#3b82f6)')} onClick={doRecoverUsername} disabled={loading}>
        {loading ? 'Sending…' : 'Send Username to Email'}
      </button>
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
      <div style={{ flex: 1, height: 1, background: '#f1f5f9' }} /><span style={{ fontSize: 11, color: '#cbd5e1', fontWeight: 600 }}>or reset password</span><div style={{ flex: 1, height: 1, background: '#f1f5f9' }} />
    </div>
    <div>
      <label style={{ fontSize: 11, fontWeight: 700, color: '#475569', display: 'block', marginBottom: 6 }}>Portal Username</label>
      <input style={inp} value={username} onChange={e => { setUsername(e.target.value); setErr(''); }} placeholder="Enter your portal username" onKeyDown={e => e.key === 'Enter' && doRequestOtp()} />
      <button style={btn('linear-gradient(135deg,#059669,#10b981)')} onClick={doRequestOtp} disabled={loading}>
        {loading ? 'Sending…' : 'Reset Password via OTP'}
      </button>
    </div>
    <button onClick={onBack} style={{ width: '100%', padding: '10px', borderRadius: 12, border: '1.5px solid #e2e8f0', background: 'transparent', color: '#64748b', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>← Back to Login</button>
  </>);

  if (step === 'username-sent') return card(<>
    {title('Username Sent ✓', 'Your username has been sent to your registered email.')}
    <div style={{ fontSize: 13, color: '#475569', marginBottom: 20 }}>Check your inbox and use it to log in.</div>
    <button style={btn()} onClick={onBack}>Back to Login</button>
  </>);

  if (step === 'otp-entry') return card(<>
    {title('Enter OTP', `OTP sent to ${maskedEmail}`)}
    {errBox}
    <input style={inp} value={otp} onChange={e => setOtp(e.target.value)} placeholder="6-digit OTP" maxLength={6} onKeyDown={e => e.key === 'Enter' && doVerifyOtp()} />
    <button style={btn()} onClick={doVerifyOtp} disabled={loading}>{loading ? 'Verifying…' : 'Verify OTP'}</button>
    <button onClick={() => { setOtp(''); setErr(''); doRequestOtp(); }} style={{ width: '100%', padding: '10px', borderRadius: 12, border: '1.5px solid #e2e8f0', background: 'transparent', color: '#1960a3', fontWeight: 600, fontSize: 12, cursor: 'pointer', marginBottom: 8 }}>Resend OTP</button>
    <button onClick={onBack} style={{ width: '100%', padding: '10px', borderRadius: 12, border: '1.5px solid #e2e8f0', background: 'transparent', color: '#64748b', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>← Back to Login</button>
  </>);

  if (step === 'new-password') return card(<>
    {title('Set New Password')}
    {errBox}
    <input style={inp} type="password" value={pw} onChange={e => setPw(e.target.value)} placeholder="New password (min 6 chars)" />
    <input style={inp} type="password" value={pwConf} onChange={e => setPwConf(e.target.value)} placeholder="Confirm new password" onKeyDown={e => e.key === 'Enter' && doResetPassword()} />
    <button style={btn()} onClick={doResetPassword} disabled={loading}>{loading ? 'Saving…' : 'Set New Password'}</button>
  </>);

  if (step === 'success') return card(<>
    {title('Password Reset ✓', 'Your password has been updated successfully.')}
    <button style={btn()} onClick={onBack}>Back to Login</button>
  </>);

  if (step === 'no-email') return card(<>
    {title('No Email Registered')}
    <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.7, marginBottom: 20 }}>
      No email address is registered for your account.<br />
      <b>Login with your credentials</b> and add your email from your profile, or <b>contact the school admin</b> to reset your password.
    </div>
    <button style={btn()} onClick={onBack}>Back to Login</button>
  </>);

  return null;
}

export default TeacherRecovery;
